import type { CellContext, ColumnDef, HeaderContext } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { Link } from 'react-router-dom';

import type { ILineItem } from '@feathr/blackbox';
import { Button, NumberCell } from '@feathr/components';
import { useLocalUrl } from '@feathr/extender/state';
import { currencyFormatter } from '@feathr/extender/utils';

/**
 * A line item plus a synthetic `id`. No field on ILineItem is a unique row key — `itemid` is an
 * Intacct type code shared across lines (every ad-campaign line is I_009), `campaign_id` is empty
 * on non-campaign lines, and feature-usage lines differ only by price/quantity — so BillTable keys
 * rows by their original array position.
 */
export type TBillRow = ILineItem & { id: string };

/**
 * Default (customer) campaign link: prefixes the route with the current account via useLocalUrl.
 * Rendered only when no `buildLineItemUrl` is supplied, so the account-context hook is never
 * invoked on the Perch path (which supplies `buildLineItemUrl`).
 */
const CampaignLink = observer(function CampaignLink({
  campaignId,
  description,
  eventId,
}: Readonly<{ campaignId: string; description: string; eventId: string }>): JSX.Element {
  const localUrl = useLocalUrl();
  return <Link to={localUrl(`/projects/${eventId}/campaigns/${campaignId}`)}>{description}</Link>;
});

/**
 * Columns for the single-bill line-item table. Only Campaign (`description`) and Total (`price`)
 * are sortable — TanStack needs an `accessorFn` for a header to sort, and `sortDescFirst: false`
 * keeps the legacy ascending-first order. Qty and Unit Price opt out with `enableSorting: false`.
 * Sorting is manual: BillTable owns `items` order.
 */
function BillColumns(
  t: TFunction,
  eventId: string,
  buildLineItemUrl?: (eventId: string, campaignId: string) => string,
): Array<ColumnDef<TBillRow>> {
  return [
    {
      id: 'description',
      accessorFn: (row): string => row.description,
      header: t('Campaign'),
      // Campaign names are the variable-length content here, so they take the width the
      // fixed numeric columns leave rather than letting it pool on the right.
      meta: { isFlex: true },
      sortDescFirst: false,
      cell: ({ row }: CellContext<TBillRow, unknown>): JSX.Element => {
        const { original } = row;
        const campaignId = original.campaign_id;
        if (!campaignId) {
          return <>{original.description}</>;
        }
        if (buildLineItemUrl) {
          return (
            <Button href={buildLineItemUrl(eventId, campaignId)} target={'_blank'} type={'link'}>
              {original.description}
            </Button>
          );
        }
        return (
          <CampaignLink
            campaignId={campaignId}
            description={original.description}
            eventId={eventId}
          />
        );
      },
    },
    {
      id: 'quantity',
      header: t('Qty'),
      size: 80,
      enableSorting: false,
      meta: { align: 'right' },
      cell: ({ row }: CellContext<TBillRow, unknown>): JSX.Element => (
        <NumberCell format={'count'} value={row.original.quantity} />
      ),
    },
    {
      id: 'unitPrice',
      header: t('Unit Price'),
      size: 120,
      enableSorting: false,
      meta: { align: 'right' },
      cell: ({ row }: CellContext<TBillRow, unknown>): JSX.Element => (
        <NumberCell format={'currency'} value={row.original.price} />
      ),
    },
    {
      id: 'price',
      accessorFn: (row): number => row.price,
      header: t('Total'),
      size: 200,
      sortDescFirst: false,
      meta: { align: 'right' },
      cell: ({ row }: CellContext<TBillRow, unknown>): JSX.Element => (
        <NumberCell format={'currency'} value={row.original.price * row.original.quantity} />
      ),
      footer: ({ table }: HeaderContext<TBillRow, unknown>): JSX.Element => {
        const total = table
          .getRowModel()
          .rows.reduce((sum, { original }) => sum + original.price * original.quantity, 0);
        return (
          <>
            <strong>{t('Amount Due:')}</strong> {currencyFormatter.format(total)}
          </>
        );
      },
    },
  ];
}

export default BillColumns;
